<template>
  <div
    :data-testid="dataTestid"
    class="flex w-full flex-col items-center gap-4 text-center"
  >
    <!--
      Height-driven with an automatic width, NOT a fixed square: the supplied artwork is a
      3:2 canvas with the shield centred in transparent padding, and forcing 113x113 on it
      squashed the shield by a third. Constraining only the height renders any export at its
      own aspect ratio, so a later re-crop needs no change here. 142px leaves the shield
      itself at roughly the 113px the enrolment success illustration uses.

      Falls back to the flat icon when no illustration is supplied, so the send-side lockout
      keeps the treatment it already had.
    -->
    <img
      v-if="image"
      :src="image"
      alt=""
      class="h-[142px] w-auto"
    />
    <V2IconsShieldRegular
      v-else
      width="48"
      height="48"
      :fill="semanticColorLookup('states.error.default')"
    />

    <h2 class="text-semantic-sizing-headline-sm font-semibold text-semantic-surface-on-surface">
      {{ title }}
    </h2>

    <p class="text-semantic-sizing-body-sm font-normal text-semantic-primary-variant-dark">
      {{ description }}
    </p>

    <p
      v-if="destination"
      class="text-semantic-sizing-body-sm font-normal text-semantic-surface-on-surface-variant"
    >
      Codes go to
      <span class="font-bold text-semantic-primary-variant-dark">{{ destination }}</span>
    </p>

    <V2AtomsButton
      id="otp_send_blocked_retry_btn"
      theme="primary"
      size="base"
      type="button"
      enable-tracking
      full
      data-testid="otp-send-blocked-retry-btn"
      :disabled="!canRetry"
      @click="emit('retry')"
    >
      {{ canRetry ? retryLabel : retryCountingLabel(secondsRemaining) }}
    </V2AtomsButton>

    <V2AtomsButton
      v-if="showNotNow"
      id="otp_send_blocked_not_now_btn"
      theme="tertiary"
      size="small"
      type="button"
      enable-tracking
      data-testid="otp-send-blocked-not-now-btn"
      @click="emit('not-now')"
    >
      {{ notNowLabel }}
    </V2AtomsButton>
  </div>
</template>

<script setup>
import { formatMmSs } from '~/utils/time/duration'

defineOptions({
  name: 'V2MoleculesOtpSendBlocked'
})

defineProps({
  title: {
    type: String,
    default: 'Too many attempts'
  },
  description: {
    type: String,
    default: 'We\'ve paused new codes to this number for your security. You can request another shortly.'
  },
  // Path to an illustration replacing the default icon, e.g. the attempt-lockout shield.
  image: {
    type: String,
    default: ''
  },
  // The masked/partial value shown to the user, e.g. "04•• ••• 560".
  destination: {
    type: String,
    default: ''
  },
  secondsRemaining: {
    type: Number,
    default: 0
  },
  canRetry: {
    type: Boolean,
    default: false
  },
  retryLabel: {
    type: String,
    default: 'Send a new code'
  },
  retryCountingLabel: {
    type: Function,
    default: seconds => `Send a new code in ${formatMmSs(seconds)}`
  },
  /*
   * Defaults true because for the SEND-side lockout this is the only way off the screen —
   * the panel replaces the whole step and carries no back control, so hiding it there
   * strands the user until the countdown ends. The attempt-lockout usages pass false to
   * match the design, and are short (ATTEMPT_LOCKOUT_SECONDS) rather than provider-length.
   */
  showNotNow: {
    type: Boolean,
    default: true
  },
  notNowLabel: {
    type: String,
    default: 'Not now'
  },
  dataTestid: {
    type: String,
    default: 'v2-molecules-otp-send-blocked'
  }
})

const emit = defineEmits(['retry', 'not-now'])
</script>
